import React from 'react';
import PropTypes from 'prop-types';
import { Container, Stack, Divider } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Rating } from '@fiverr-private/orca';
import { I18n } from '@fiverr-private/i18n-react';
import { Box } from '@fiverr-private/theme';
import Description from '../../../../shared/Description';
import SellerResponse from '../../SellerResponse';
import TranslationButton from '../../../../shared/TranslationButton';
import { GigInfo, GigInfoPropType } from '../../../../FreelancerReviews/FreelancerReviewItem/GigInfo';
import { WorkSample } from '../../../../shared/WorkSample';
import { ReliableReviewHeader } from './ReliableReviewHeader';
import { scoreClass, wrapperClass } from './ReliableReviewItem.css';

const DesktopView = ({
    user,
    createdAt,
    showBusinessBadge,
    isRepeatBuyer,
    isCancelledOrder,
    countryCode,
    ratingScore,
    descriptionText,
    shouldHighlight,
    priceRange,
    orderDuration,
    orderTypeHourly,
    displayTranslationButton,
    translationConfig,
    componentName,
    workSampleData,
    gigData,
    reviewerIndustry,
    sellerResponse,
}) => (
    <Container marginTop="8" borderWidth="sm" borderRadius="2xl" className={wrapperClass}>
        <Stack withDivider gap="4" paddingX="6" paddingY="4" direction="column">
            <ReliableReviewHeader
                user={user}
                showBusinessBadge={showBusinessBadge}
                reviewerIndustry={reviewerIndustry}
                isRepeatBuyer={isRepeatBuyer}
                isCancelledOrder={isCancelledOrder}
                countryCode={countryCode}
                createdAt={createdAt}
            />
            <Stack gap="10" direction="row" justifyContent="spaceBetween">
                <Stack
                    gap="3"
                    direction="column"
                    maxWidth="100%"
                    // minWidth is set to make flexbox shrink properly
                    // Details:
                    // https://www.bigbinary.com/blog/understanding-the-automatic-minimum-size-of-flex-items
                    minWidth={0}
                >
                    {isCancelledOrder && (
                        <Text color="bodySecondary" size="b_xs">
                            <I18n k="reviews.general.order_canceled" />
                        </Text>
                    )}
                    <Stack withDivider direction="row" alignItems="center" dividerProps={{ variant: 'dot' }}>
                        <Rating score={ratingScore} color={Rating.COLORS.BLACK} scoreClassName={scoreClass} />
                        <Text size="b_xs" color="bodySecondary">
                            <time>{createdAt}</time>
                        </Text>
                    </Stack>
                    <Container maxWidth="1100px">
                        <Description
                            text={descriptionText}
                            shouldHighlight={shouldHighlight}
                            maxLength={300}
                            displayLess
                        />
                    </Container>
                    {(priceRange || orderDuration || orderTypeHourly || gigData) && (
                        <Stack withDivider marginTop="0" gap="10" direction="row" dividerProps={{ marginY: '2' }}>
                            {priceRange ? <Stat value={priceRange} title={<I18n k="reviews.gigs.price" />} /> : null}
                            {orderDuration ? (
                                <Stat value={orderDuration} title={<I18n k="reviews.gigs.duration" />} />
                            ) : null}
                            {orderTypeHourly ? (
                                <Stat
                                    value={<I18n k="reviews.gigs.order_type_hourly" />}
                                    title={<I18n k="reviews.gigs.order_type" />}
                                />
                            ) : null}
                            {gigData ? <GigInfo gig={gigData} /> : null}
                        </Stack>
                    )}
                    {displayTranslationButton ? (
                        <Container marginTop="4">
                            <TranslationButton translationConfig={translationConfig} componentName={componentName} />
                        </Container>
                    ) : null}
                </Stack>
                {workSampleData?.previewUrl ? (
                    <Box flexShrink={0} flexBasis={144}>
                        <WorkSample {...workSampleData} />
                    </Box>
                ) : null}
            </Stack>
        </Stack>
        {sellerResponse ? (
            <Stack direction="column" paddingX="6" gap="0">
                <Divider />
                <SellerResponse response={sellerResponse} ratingScore={ratingScore} />
            </Stack>
        ) : null}
    </Container>
);

DesktopView.propTypes = {
    review: PropTypes.shape({
        created_at: PropTypes.string,
        comment: PropTypes.string,
        reviewer_country_code: PropTypes.string,
        username: PropTypes.string,
        value: PropTypes.number,
        is_business: PropTypes.bool,
        reviews_count_as_buyer: PropTypes.number,
        price: PropTypes.string,
        duration: PropTypes.string,
        order_type: PropTypes.string,
    }).isRequired,
    user: PropTypes.object.isRequired,
    createdAt: PropTypes.string.isRequired,
    showBusinessBadge: PropTypes.bool.isRequired,
    countryCode: PropTypes.string.isRequired,
    ratingScore: PropTypes.number.isRequired,
    descriptionText: PropTypes.string.isRequired,
    shouldHighlight: PropTypes.bool.isRequired,
    priceRange: PropTypes.string,
    orderDuration: PropTypes.string,
    orderTypeHourly: PropTypes.bool,
    displayTranslationButton: PropTypes.bool.isRequired,
    isRepeatBuyer: PropTypes.bool.isRequired,
    isCancelledOrder: PropTypes.bool.isRequired,
    translationConfig: PropTypes.object.isRequired,
    componentName: PropTypes.string.isRequired,
    workSampleData: PropTypes.shape({
        workSample: PropTypes.string,
        smallWorkSample: PropTypes.string,
        position: PropTypes.number,
    }),
    gigData: GigInfoPropType,
    reviewerIndustry: PropTypes.array,
    sellerResponse: PropTypes.object,
};

const Stat = ({ value, title }) => (
    <Stack gap="0" direction="column" justifyContent="flexStart">
        <Text fontWeight="semibold" size="b_sm">
            {value}
        </Text>
        <Text color="bodySecondary" size="b_xs" minWidth="auto">
            {title}
        </Text>
    </Stack>
);

export default DesktopView;
